import { RequestMethod, Response, ResponseOptions } from '@angular/http';
import { FileError } from '@ionic-native/file';
import { isNumber, isString } from 'ionic-angular/util/util';
import { Observable } from 'rxjs/Observable';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { of } from 'rxjs/observable/of';
import { _throw } from 'rxjs/observable/throw';
import { Subject } from 'rxjs/Subject';
import { VASRestService } from '../../../core/services/api/vas-rest';
import { FileChangeQItem } from '../../../core/services/sync-up/file-item';
import { FileUploadHelperInterface } from './file-upload-helper.interface';

export enum FileUploaderState {
    NOT_READY,
    SENDING,
    SUCCESS,
    ERROR,
    COMPLETE
}

export interface FileUploaderInfo {
    state: FileUploaderState;
    chunk: number;
    uploadUrl: string;
    response: Response;
}

export class FileUploader {

    // this is the minimum size of chunk we're allowed to send which is defined
    // by S3. Files smaller than this must be sent in one request.
    protected static MIN_CHUNK_SIZE = 5 * 1024 * 1024;
    protected state: FileUploaderState = FileUploaderState.NOT_READY;
    protected progress$ = new Subject<FileUploaderInfo>();
    protected chunkIndex = 0;
    protected maxChunks: number;
    protected chunkSize = 1024 * 1024 * 6;
    protected response: Response;
    error: any;

    protected RETRY_FROM_START = 'Failed; retry from start';
    protected RESUME_FROM_PART = 'Resume from part';

    static fileChangeQItemToUploadUrl(
        item: FileChangeQItem,
        api: VASRestService
    ): Observable<string> {

        if (item.fileSize <= FileUploader.MIN_CHUNK_SIZE) {
            return of([
                `/accounts/${item.account}`,
                `/jobs/${item.parentId}`,
                `/files/${item.id}`,
                '/content'
            ].join(''));
        }

        return FileUploader.createMultipartUploadSession(
            item.account,
            item.parentId,
            item.id,
            item.fileSize,
            item.mimeType,
            api
        );
    }

    static createMultipartUploadSession(
        accountId: string,
        jobId: string,
        fileId: string,
        fileSize: number,
        mimeType: string,
        api: VASRestService
    ): Observable<string> {
        return api
            .createRequest(
                true,
                `/accounts/${accountId}/jobs/${jobId}/uploads`,
                RequestMethod.Post
            )
            .setBody({
                upload: {
                    file: fileId,
                    fileSize,
                    mimeType
                }
            })
            .execute()
            .map(response =>
                response.headers
                    .get('Location')
                    .replace('/api/v1', '')
            )
            .catch(error => {
                if (error.status === 409) {
                    return of(undefined);
                }
                return _throw(error);
            });
    }

    constructor(
        protected fileSize: number,
        protected localFileSystemPath: string,
        protected name: string,
        protected chunk: number,
        protected uploadUrl: string,
        protected api: VASRestService,
        protected helper: FileUploadHelperInterface
    ) {

    }

    upload(): Observable<FileUploaderInfo> {

        if (!isNumber(this.fileSize) || !this.fileSize) {
            return _throw(new Error('fileSize must be greater than zero'));
        }

        if (
            !isString(this.localFileSystemPath) ||
            !this.localFileSystemPath.length
        ) {
            return _throw(
                new Error(
                    `localFileSystemPath '${this.localFileSystemPath}' is invalid`
                )
            );
        }

        if (!isString(this.name) || !this.name.length) {
            return _throw(new Error(`name '${this.name}' is invalid`));
        }

        if (!isNumber(this.chunkIndex)) {
            return _throw(new Error('chunk is not instance of number'));
        }

        setTimeout(() => {
            this.next(FileUploaderState.NOT_READY)
                .catch(error => {
                    this.emitError(error);
                    return of(undefined);
                })
                .subscribe();
        });

        return this.progress$;
    }

    protected next(state: FileUploaderState): Observable<FileUploaderState> {
        this.state = state;
        this.emitProgress();
        switch (state) {
            case FileUploaderState.NOT_READY: {
                return fromPromise(
                    this.prepareFile(this.localFileSystemPath)
                )
                    .concatMap(() => {

                        if (
                            this.fileSize >= FileUploader.MIN_CHUNK_SIZE &&
                            this.chunkIndex >= this.maxChunks
                        ) {
                            return this.next(FileUploaderState.COMPLETE);
                        }

                        return this.next(FileUploaderState.SENDING);
                    });
            }
            case FileUploaderState.SENDING: {

                if (this.fileSize < FileUploader.MIN_CHUNK_SIZE) {
                    return this.sendBinary()
                        .catch(error => {
                            // if we have already sent this file, ignore the error
                            if (error.status === 409) {
                                return of(new Response(new ResponseOptions({
                                    body: undefined,
                                    status: 201,
                                    headers: undefined,
                                    url: undefined
                                })));
                            }
                            return _throw(error);
                        })
                        .do(response =>
                            this.response = response
                        )
                        .concatMap(() =>
                            this.next(FileUploaderState.SUCCESS)
                        );
                }

                return this.sendChunk(this.chunkIndex)
                    .do(response => this.response = response)
                    .concatMap(() => {

                        this.chunkIndex++;

                        if (this.chunkIndex >= this.maxChunks) {
                            return this.next(FileUploaderState.SUCCESS);
                        }

                        return this.next(FileUploaderState.SENDING);
                    });
            }
            case FileUploaderState.SUCCESS: {
                return this.next(FileUploaderState.COMPLETE);
            }
            case FileUploaderState.COMPLETE: {
                this.progress$.complete();
                return of(FileUploaderState.COMPLETE);
            }
            case FileUploaderState.ERROR: {
                return of(FileUploaderState.COMPLETE);
            }
            default: {
                return _throw(new Error(`invalid state`));
            }
        }
    }

    protected emitProgress(): void {
        this.progress$.next({
            state: this.state,
            chunk: this.chunkIndex,
            uploadUrl: this.uploadUrl,
            response: this.response
        });
    }

    protected emitError(error: Error): void {
        this.progress$.error(error);
    }

    protected async prepareFile(path: string): Promise<any> {
        this.maxChunks = Math.ceil(this.fileSize / this.chunkSize);
        return this.helper.prepare(path);
    }

    protected sendBinary(): Observable<Response> {
        return this.helper.sendBinary(
            this.localFileSystemPath,
            this.uploadUrl
        );
    }

    protected sendChunk(chunkIndex: number): Observable<Response> {
        let _blob: Blob;
        return fromPromise(this.readChunk(chunkIndex))
            .concatMap(blob => {
                _blob = blob;
                return this._sendChunk(blob, chunkIndex);
            })
            .catch((error: Response | FileError) => {
                switch (true) {
                    case error instanceof Response: {
                        return this.handleErrorResponse(
                            error as Response,
                            _blob
                        );
                    }
                    default: {
                        return _throw(error);
                    }
                }
            });
    }

    protected _sendChunk(blob: Blob, chunkIndex: number): Promise<Response> {
        console.log('[FileUpload] send chunk %s', chunkIndex);
        return this.api.createRequest(
            true,
            this.uploadUrl,
            RequestMethod.Put
        )
            .appendHeader('X-Part', (chunkIndex + 1).toString())
            .appendHeader('Content-type', 'application/octet-stream')
            .setBody(blob)
            .execute()
            .toPromise();
    }

    protected async readChunk(index: number): Promise<Blob> {
        console.log('[FileUpload] read chunk %s', index);
        return this.helper.readChunk(
            this.localFileSystemPath,
            index,
            this.chunkSize
        ).toPromise();
    }

    protected handleErrorResponse(
        error: Response,
        blob: Blob
    ): Observable<Response> {
        const message: string = error.json().message || '';
        switch (true) {
            case error.status === 400 &&

            message.startsWith(this.RESUME_FROM_PART): {

                const nextChunk = parseInt(
                    message.match(/(\d*)$/)[0],
                    10
                );

                if (isNaN(nextChunk)) {
                    return _throw(error);
                }

                return fromPromise(this._sendChunk(blob, nextChunk - 1));
            }
            case error.status === 400 && message === this.RETRY_FROM_START: {
                return fromPromise(this._sendChunk(blob, 0));
            }
            default: {
                return _throw(error);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/lib/file-uploader/file-uploader.ts